// Provide custom SSO capabilities for ChamberMaster
// If feature is not configured, just renders children as is.
import React, { useEffect, useMemo, useState } from 'react';
import { WebStorageStateStore } from 'oidc-client-ts';
import {
  useGetChamberMasterIntegrationEnabled,
  useLoginChamberMaster,
  useStartLoginChamberMaster,
} from '@juno/client-api';
import { Platform, Site } from '@juno/client-api/model';

type ChamberMasterProviderProps = {
  children: React.ReactNode;
  platform: Platform;
  site: Site;
  userStore: WebStorageStateStore;
  isAdminLogin: boolean;
};

export const ChamberMasterProvider: React.FC<ChamberMasterProviderProps> = ({
  children,
  platform,
  site,
  userStore,
  isAdminLogin,
}) => {
  const tokenGuidParam = useMemo(() => {
    return new URLSearchParams(window.location.search).get('token');
  }, []);
  const codeParam = useMemo(() => {
    return new URLSearchParams(window.location.search).get('code');
  }, []);
  const startLoginChamberMaster = useStartLoginChamberMaster();
  const loginChamberMaster = useLoginChamberMaster();
  const { data: isChamberMasterEnabledResponse, isLoading: isChamberMasterCheckLoading } =
    useGetChamberMasterIntegrationEnabled(site?.id || '', { query: { enabled: !!site?.id } });
  const isChamberMasterEnabled = useMemo(() => {
    return !!isChamberMasterEnabledResponse?.enabled;
  }, [isChamberMasterEnabledResponse]);
  const [loggedIn, setLoggedIn] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    // Check whether the user is logged in.
    const authority = process.env.NX_FUSIONAUTH_URL || 'UNSET_NX_FUSIONAUTH_URL';
    userStore.get(`user:${authority}:${platform.id}`).then((value) => setLoggedIn(!!value));
  }, [platform, userStore]);

  const savePostAuthRedirect = () => {
    // Save the current url without the token to redirect later. Don't overwrite if already saved.
    if (sessionStorage.getItem('postAuthRedirectUrl')) {
      return;
    }
    // Clean up and save the post auth redirect in storage
    const postAuthRedirectUrl = new URL(window.location.href);
    const params = new URLSearchParams(postAuthRedirectUrl.search);
    params.delete('token');
    const paramsString = params.toString() ? `?${params.toString()}` : '';
    sessionStorage.setItem(
      'postAuthRedirectUrl',
      `${postAuthRedirectUrl.origin}${postAuthRedirectUrl.pathname}${paramsString}`,
    );
    const fiveMinutesLater = Date.now() + 5 * 60 * 1000;
    sessionStorage.setItem('postAuthRedirectUrlTimeout', fiveMinutesLater.toString());
  };
  const handleChamberMasterLogin = async () => {
    // Login with ChamberMaster then direct user to the generated magic link
    const { protocol, hostname, port } = window.location;
    const baseUri = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    const redirectUri = `${baseUri}/${site?.slug}/callback`;

    savePostAuthRedirect();

    loginChamberMaster
      .mutateAsync({
        siteId: site?.id || '',
        data: {
          token: tokenGuidParam || '',
          redirect_uri: redirectUri,
        },
      })
      .then((response) => {
        // We get a magic link we can send the user to so they can log into FusionAuth
        // but first we need to store the code verifier so our OIDC auth provider can
        // verify the code when it gets returned
        userStore.set(
          response.randomized_id,
          JSON.stringify({
            id: response.randomized_id,
            created: Math.floor(Date.now() / 1000),
            request_type: 'si:r',
            code_verifier: response.code_verifier,
            authority: process.env.NX_FUSIONAUTH_URL || 'UNSET_NX_FUSIONAUTH_URL',
            client_id: platform?.id || 'UNSET_CLIENT_ID',
            redirect_uri: redirectUri,
            scope: 'openid profile',
            extraTokenParams: {},
            response_mode: 'query',
          }),
        );
        // Now we can navigate the user to the magic link provided to log them into FA
        window.open(response.magic_link, '_self');
      });
  };

  const initiateChamberMasterLogin = async () => {
    // Redirect the user to ChamberMaster so they can login with Reverse SSO
    // The backend provide the configured url for now.

    savePostAuthRedirect();
    startLoginChamberMaster
      .mutateAsync({
        siteId: site?.id || '',
        data: {
          redirect_uri: window.location.href,
        },
      })
      .then((response) => {
        window.open(response.redirect_uri, '_self');
      });
  };

  useEffect(() => {
    if (isAdminLogin) {
      // User will go directly to FusionAuth. Do not start CM login.
      return;
    }
    if (!isChamberMasterEnabled) {
      return;
    }
    // Incoming code from ChamberMaster. Process login.
    if (tokenGuidParam) {
      handleChamberMasterLogin();
    }
    // User went to community directly without being logged in, initiate a reverse SSO
    else if (!codeParam && loggedIn === false) {
      initiateChamberMasterLogin();
    }
  }, [isAdminLogin, isChamberMasterEnabled, tokenGuidParam, codeParam, loggedIn]);

  // Also check for a logged in state
  if (
    !isAdminLogin &&
    (isChamberMasterCheckLoading ||
      (isChamberMasterEnabled &&
        // Check for token for ChamberMaster. Handled in hook above.
        (!!tokenGuidParam ||
          // Check for non-logged in without incoming code (from FA), also handled in hook.
          (!codeParam && loggedIn === false))))
  ) {
    return null;
  }
  return <div>{children}</div>;
};

export default ChamberMasterProvider;
